import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Loader, Text } from '@mantine/core';
import axiosInstance from '../axiosInstance';
import MyUserContext from '../myUserContext';

export default function UserQuotesList({ userId, language, query = '' }) {
  const myUser = useContext(MyUserContext);
  const viewerId = myUser?.myUserInfo?.myUserId;
  // Remount on account/profile changes so private results cannot survive switching users.
  return <Quotes key={`${userId}:${viewerId || ''}`} userId={userId} language={language} query={query} isMine={viewerId === userId} />;
}

function Quotes({ userId, language, isMine, query }) {
  const t = (ru, en) => language === 'ru' ? ru : en;
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const version = ++generation.current;
    const controller = new AbortController();
    setItems([]); setHasMore(false); setBusy(true); setError(false);
    const timer = setTimeout(() => {
      axiosInstance.get(`/users/${userId}/quotes`, { params: { q: query.trim() }, signal: controller.signal })
        .then(({ data }) => { if (version === generation.current) { setItems(data.items); setHasMore(data.hasMore); } })
        .catch(() => { if (!controller.signal.aborted && version === generation.current) setError(true); })
        .finally(() => { if (version === generation.current && !controller.signal.aborted) setBusy(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); generation.current++; };
  }, [userId, query, retry]);

  const more = async () => {
    const version = generation.current;
    setBusy(true); setError(false);
    try {
      const { data } = await axiosInstance.get(`/users/${userId}/quotes`, { params: { q: query.trim(), offset: items.length } });
      if (version === generation.current) { setItems(prev => [...prev, ...data.items]); setHasMore(data.hasMore); }
    } catch (_) { if (version === generation.current) setError(true); }
    finally { if (version === generation.current) setBusy(false); }
  };

  return <section className="mx-auto max-w-2xl px-2 pb-8" aria-label={t('Цитаты', 'Quotes')}>
    {items.map(q => <article key={q.id} style={{ borderBottom: '1px solid var(--mantine-color-default-border)', padding: '16px 0' }}>
      <blockquote style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 19, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '0 0 12px', paddingLeft: 16, borderLeft: '3px solid var(--mantine-color-default-border)' }}>{q.text}</blockquote>
      <Link to={`/arts/${q.art.id}`} style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
        {q.art.thumbnailUrl && <img src={q.art.thumbnailUrl} alt="" loading="lazy" style={{ width: 32, height: 48, objectFit: 'cover', borderRadius: 4 }} />}
        <div><Text size="sm" fw={500}>{(language === 'ru' && q.art.ruName) || q.art.name || t('Книга', 'Book')}</Text>{q.art.author && <Text size="xs" c="dimmed">{q.art.author}</Text>}</div>
      </Link>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Link to={`/quotes/${q.id}`} style={{ color: 'var(--mantine-color-dimmed)', fontSize: 12 }}>{new Date(q.createdTime).toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' })}</Link>
        {isMine && <Text size="xs" c="dimmed">· {q.isPublic ? t('Публичная', 'Public') : t('Только вы', 'Only you')}</Text>}
      </div>
    </article>)}
    {busy && <div className="flex justify-center py-6"><Loader size="sm" /></div>}
    {error && <div className="text-center py-4"><Text c="dimmed" size="sm">{t('Не удалось загрузить цитаты.', 'Could not load quotes.')}</Text><Button variant="subtle" onClick={() => items.length ? more() : setRetry(n => n + 1)}>{t('Повторить', 'Retry')}</Button></div>}
    {!busy && !error && !items.length && <Text c="dimmed" ta="center" my="xl">{query.trim() ? t('Цитаты не найдены.', 'No quotes found.') : isMine ? t('У вас пока нет цитат.', 'You have no saved quotes yet.') : t('Публичных цитат пока нет.', 'No public quotes yet.')}</Text>}
    {!busy && !error && hasMore && <Button variant="subtle" fullWidth mt="md" onClick={more}>{t('Показать ещё', 'Show more')}</Button>}
  </section>;
}
